import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Chip, Divider, Grid, Typography } from '@mui/material';
import { format, parse } from 'date-fns';
import { Form, Formik } from 'formik';
import React, { forwardRef, useCallback, useContext, useImperativeHandle, useRef, } from 'react';
import { isBreakpointMobile, useBreakpoint } from '../../breakpoint';
import { DateField } from '../../components';
import { defaultWorkingDays } from '../../types/calendarTypes';
import { DayPickerForm } from '../../UserDataManagement';
import { EndDate } from '../../UserDataManagement';
import { introHolidayYearFormSchemaFunc, } from '../../yup-schemas';
import { HolidayYearActionKind, IntroSetupContext } from '../reducer';
const DATE_FORMAT = 'dd/MM/yyyy';
// eslint-disable-next-line react/display-name
export const HolidayYearPage = forwardRef((props, ref) => {
    const breakpoint = useBreakpoint();
    const isMobile = isBreakpointMobile(breakpoint);
    const { holidayYearDispatch, holidayYearState } = useContext(IntroSetupContext);
    const [workingDays, setWorkingDays] = React.useState(defaultWorkingDays);
    const [leftChipSelected, setLeftChipSelected] = React.useState(false);
    const [rightChipSelected, setRightChipSelected] = React.useState(false);
    React.useEffect(() => {
        if (props.initial === false && holidayYearState.workingDays) {
            setWorkingDays(holidayYearState.workingDays);
        }
        else {
            setWorkingDays({
                monday: true,
                tuesday: true,
                wednesday: true,
                thursday: true,
                friday: true,
                saturday: false,
                sunday: false,
            });
        }
    }, [holidayYearState.workingDays, props.initial]);
    useImperativeHandle(ref, () => ({
        handleSubmit() {
            formRef.current.handleSubmit();
        },
        getIsValid() {
            return formRef.current.values[EndDate] && formRef.current.isValid;
        },
    }));
    /* eslint-disable  @typescript-eslint/no-explicit-any */
    const formRef = useRef();
    const handleSubmit = useCallback((values) => {
        holidayYearDispatch({
            type: HolidayYearActionKind.ModifyEndDate,
            payload: values.end,
        });
        holidayYearDispatch({
            type: HolidayYearActionKind.ModifyWorkingDays,
            payload: workingDays,
        });
    }, [holidayYearDispatch, workingDays]);
    const setDate = useCallback((date) => {
        formRef.current.setFieldValue(EndDate, parse(date, DATE_FORMAT, new Date()));
    }, []);
    const onWorkingDaysChange = useCallback((value) => {
        setWorkingDays(value);
    }, []);
    const currentYear = new Date().getFullYear();
    const currentDate = new Date();
    const startOfYear = new Date(currentYear, 0, 1);
    const marchDate = new Date(currentYear, 2, 31);
    const marchDateText = format(marchDate, DATE_FORMAT);
    const endOfYear = new Date(currentYear, 11, 31);
    const endOfYearText = format(endOfYear, DATE_FORMAT);
    const marchNextYear = new Date(currentYear + 1, 2, 31);
    const marchNextYearDateText = format(marchNextYear, DATE_FORMAT);
    const endOfNextYear = new Date(currentYear + 1, 11, 31);
    const endOfNextYearText = format(new Date(endOfNextYear), DATE_FORMAT);
    let leftText = '';
    let rightText = '';
    if (currentDate >= startOfYear && currentDate < marchDate) {
        leftText = marchDateText;
        rightText = endOfYearText;
    }
    else if (currentDate >= marchDate && currentDate < endOfYear) {
        leftText = endOfYearText;
        rightText = marchNextYearDateText;
    }
    else {
        leftText = marchNextYearDateText;
        rightText = endOfNextYearText;
    }
    const onDateFieldChange = useCallback(() => {
        setLeftChipSelected(false);
        setRightChipSelected(false);
    }, []);
    const leftButtonOnClick = useCallback(() => {
        setLeftChipSelected(true);
        setRightChipSelected(false);
        setDate(leftText);
    }, [leftText, setDate]);
    const rightButtonOnClick = useCallback(() => {
        setRightChipSelected(true);
        setLeftChipSelected(false);
        setDate(rightText);
    }, [rightText, setDate]);
    return (_jsx(Box, { children: _jsx(Grid, { container: true, justifyContent: "center", children: _jsx(Formik, { initialValues: { end: props.initial ? null : holidayYearState.end }, validationSchema: introHolidayYearFormSchemaFunc, innerRef: formRef, onSubmit: handleSubmit, children: _jsx(Form, { children: _jsxs(Grid, { container: true, rowSpacing: 1, children: [_jsx(Grid, { item: true, children: _jsx(Typography, { variant: "body1", sx: { mb: 0, mt: 2.5 }, children: "When is the last day of your current holiday year?" }) }), _jsx(Grid, { item: true, xs: 12, sx: { marginBottom: '-1em' }, children: _jsx(Typography, { variant: "body1", sx: { mb: 1.5, mt: 1 }, children: "Quick select" }) }), _jsx(Grid, { container: true, item: true, xs: 12, justifyContent: "center", children: _jsxs(Box, { alignContent: "center", display: 'flex', justifyContent: "center", children: [_jsx(Chip, { sx: { pt: '2px' }, color: 'primary', variant: leftChipSelected ? 'filled' : 'outlined', onClick: leftButtonOnClick, label: leftText }), _jsx(Divider, { orientation: "vertical", sx: {
                                                mx: isMobile ? 1 : 6,
                                                borderColor: 'transparent',
                                                backgroundColor: 'transparent',
                                            }, flexItem: true, light: true }), _jsx(Chip, { sx: { pt: '2px' }, color: 'primary', variant: rightChipSelected ? 'filled' : 'outlined', onClick: rightButtonOnClick, label: rightText })] }) }), _jsx(Grid, { item: true, xs: 12, children: _jsx(DateField, { onChange: onDateFieldChange, label: "End Date", name: "end", required: true, sx: { width: '100%' }, isLoading: false, placeholder: 'dd/mm/yyyy' }) }), _jsx(Grid, { item: true, xs: 12, marginTop: 2, children: _jsx(DayPickerForm, { value: props.initial ? defaultWorkingDays : workingDays, onChange: onWorkingDaysChange }) })] }) }) }) }) }));
});
